import CloseIcon from '@mui/icons-material/Close';
import {
  Grid,
  Typography,
  Dialog,
  FormHelperText,
  DialogContent,
  DialogTitle,
  Stack,
  DialogActions,
  Box,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { ErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import {
  uploadFileToS3,
  processExcelRecords,
  readExcelRecords,
  getPendingRecords,
  ICVBulkUploadDTO,
  getProcessedExcelRecordsByFileId,
  ISubmissionSettingsResponse,
  deleteExcelRecord,
  IFile,
  getRecruiterTemplates,
  IExcelImportTemplate,
} from 'wisestep-api';
import {
  ArrowButton,
  FileTypeVariants,
  Label,
  WsButton,
  WsColors,
  WsFileUpload,
  WsIconDownload,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsSelectObj,
} from 'wisestep-base';

import { ConfirmDeleteDialog } from '../../../../components';
import { IExcelRecords } from '../../../../features/importCandidates/types';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { downloadBulkSubmissionsSampleFile } from '../../utils/bulkSubmissionsHelper';
import ImportSpreadSheetColumns from '../importSubmissions/ImportSpreadSheetColumns';

import SaveExcelTemplateModel from './SaveExcelTemplateModel';
import UploadSpreadSheetsColumns from './UploadSpreadSheetsColumns';

interface FileUploadProgress {
  file: File;
  progress?: number;
  error?: string;
  processing?: boolean;
  completed?: boolean;
}

interface IBulkUploadSpreadSheets {
  isSubmitted: boolean;
  uploadingFor?: 'submissions' | 'mapping';
  submissionSetting?: ISubmissionSettingsResponse;
  getFieldMappingValues: (columns: string[], ignoreFirstRow: boolean) => void;
  openMappingDialog: boolean;
  setOpenMappingDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

const BulkUploadSpreadSheets = ({
  isSubmitted,
  uploadingFor = 'mapping',
  submissionSetting,
  getFieldMappingValues,
  openMappingDialog,
  setOpenMappingDialog,
}: IBulkUploadSpreadSheets) => {
  const methods = useFormContext();
  const {
    setValue,
    getValues,
    formState: { errors },
    clearErrors,
  } = methods;
  const [selectedCandidatesFile, setSelectedCandidatesFile] = useState<FileUploadProgress | null>();
  const [pendingFiles, setPendingFiles] = useState<ICVBulkUploadDTO[]>([]);

  const [excelFields, setExcelFields] = useState<Map<number, IExcelRecords>>();

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  const [loadTemplates, setLoadTemplates] = useState<boolean>(false);

  const [templates, setTemplates] = useState<IExcelImportTemplate[]>([]);
  const [templateOption, setTemplateOptions] = useState<WsSelectObj[]>([]);

  const [recentTemplates, setRecentTemplates] = useState<WsSelectObj[]>([]);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const [isTemplateSelected, setIsTemplateSelected] = useState<boolean>(false);

  const fileToBeDeleted = useRef<IFile>({ name: '', location: '' });

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);

  const selectedMenuItemStyle = {
    backgroundColor: '#caf0f8',
    cursor: 'pointer',
  };

  const onFileUpload = async (files: File[]) => {
    setSelectedCandidatesFile({ file: files[0], progress: 0 });
    setOpenMappingDialog(false);
    try {
      const response = await uploadFileToS3(files[0], (event: any) => {
        const progress = { ...selectedCandidatesFile, file: files[0] };
        progress.progress = (100 * event.loaded) / event.total;
        setSelectedCandidatesFile(progress);
      });
      const url = response;
      const processRequest: ICVBulkUploadDTO = {
        resumeFileName: files[0].name,
        resumeURL: url.split('?')[0],
        isJobCandidateMapping: uploadingFor === 'mapping',
      };
      setSelectedCandidatesFile({
        ...selectedCandidatesFile,
        progress: selectedCandidatesFile?.progress,
        file: files[0],
        processing: true,
      });
      const processResponse = await processExcelRecords(processRequest);
      const filedId = processResponse.data.fileId;
      setSelectedCandidatesFile({
        ...selectedCandidatesFile,
        progress: selectedCandidatesFile?.progress,
        file: files[0],
        completed: true,
      });
      await readExcelRecordsByFileId(filedId);
    } catch (e) {
      setNotification(
        failed('The data format provided is invalid. Please try again using a valid format.')
      );
      setSelectedCandidatesFile(null);
    }
  };

  const readExcelRecordsByFileId = async (fileId: string) => {
    setValue('fileId', fileId);
    try {
      const excelResponse =
        uploadingFor == 'mapping'
          ? await readExcelRecords(fileId)
          : await getProcessedExcelRecordsByFileId(fileId);

      setExcelFields(excelResponse.data);
      setIsTemplateSelected(false);
    } catch (e) {
      console.log(e);
    }
  };

  const onPendingFileSelect = async (fileId: string) => await readExcelRecordsByFileId(fileId);

  const onFileRejected = (files: FileRejection[]) => {
    files.map((file) => {
      file.errors.map((error) => {
        console.log('rejected' + error.message);
      });
    });
  };

  const deleteExcelFileById = async () => {
    try {
      if (fileToBeDeleted.current) {
        await deleteExcelRecord(fileToBeDeleted.current.location);
        setOpenDeleteDialog(false);
        setNotification(success('File deleted successfully !!'));
        fetchPendingFiles();
      } else {
        setNotification(warning('File Id not found, Please try again !!'));
      }
    } catch {
      setNotification(failed('Something went wrong , Please try again !!'));
    }
  };

  const fetchPendingFiles = useCallback(async () => {
    const response = await getPendingRecords(uploadingFor === 'mapping');
    setPendingFiles(response.data);
  }, [uploadingFor]);

  useEffect(() => {
    fetchPendingFiles();
    if (isSubmitted) setSelectedCandidatesFile(null);
  }, [fetchPendingFiles, isSubmitted]);

  useEffect(() => {
    (async () => {
      if (loadTemplates || templates.length === 0) {
        if (loadTemplates) {
          setLoadTemplates(false);
        }
        const response = await getRecruiterTemplates(uploadingFor === 'mapping');
        const templateData = response.data.filter((data) => !data.isHidden);

        templateData.sort(
          (template1: any, template2: any) =>
            Number(new Date(template2.createdOn)) - Number(new Date(template1.createdOn))
        );

        // Take the top 4 templates for recent templates
        const recentTemplates = templateData.slice(0, 4);

        if (response.status === 200) {
          setTemplates(templateData);
          setTemplateOptions(
            templateData?.map((template, index) => {
              return {
                label: template.name ?? '',
                value: index,
              };
            }) ?? []
          );
          setRecentTemplates(
            recentTemplates?.map((template, index) => {
              return {
                label: template.name ?? '',
                value: index,
              };
            }) ?? []
          );
        }
      }
    })();
  }, [uploadingFor, loadTemplates, templates.length]);

  const onTemplateSelect = (event: SelectChangeEvent<number | string>) => {
    if (templates && event.target.value >= 0) {
      setSelectedTemplateIndex(Number(event.target.value));
      setIsTemplateSelected(true);
      const formData = methods.getValues();
      methods.reset({
        ...formData,
        selectedTemplate: Number(event.target.value),
        fieldMappings: templates[Number(event.target.value)].columnMapping,
      });
    }
  };

  const resetTemplate = () => {
    setSelectedTemplateIndex(-1);
    setIsTemplateSelected(false);
    const formData = methods.getValues();
    methods.reset({
      ...formData,
      selectedTemplate: -1,
      fieldMappings: [],
    });
  };

  return (
    <Grid>
      <Grid>
        <WsFileUpload
          name="CandidateSpreadSheets"
          onUpload={onFileUpload}
          placeholderText={'Drag & drop .xls,.xlvs or .csv file < 1MB'}
          fileTypes={FileTypeVariants.BULKUPLOAD}
          onFileRejected={onFileRejected}
          maxFileSize={2}
        />
        {pendingFiles.map((data, index) => (
          <Grid key={index} container px={2} pt={2}>
            <Grid item xs={5} display="flex" justifyContent="center">
              <Typography>{data.resumeFileName}</Typography>
            </Grid>
            <Grid item xs={1} pt={0.5}>
              <Tooltip title="Delete File">
                <CloseIcon
                  color="error"
                  fontSize="small"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    fileToBeDeleted.current = {
                      name: data?.resumeFileName ?? '',
                      location: data?.id ?? '',
                    };
                    setOpenDeleteDialog(true);
                  }}
                />
              </Tooltip>
            </Grid>
            <Grid item xs={2} display="flex" justifyContent="center">
              <WsButton onClick={() => onPendingFileSelect(data.id ?? '')}>Import File</WsButton>
            </Grid>
            {data.id === getValues('fileId') && (
              <>
                <Grid item xs={2} pl={5}>
                  <WsButton type="submit">Confirm Import</WsButton>
                  {errors.fieldMappings && (
                    <FormHelperText error>{errors.fieldMappings.message}</FormHelperText>
                  )}
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="flex-end">
                  <WsButton onClick={() => setOpenMappingDialog(true)}>View File</WsButton>
                </Grid>
              </>
            )}
          </Grid>
        ))}

        {selectedCandidatesFile && (
          <>
            <Grid container px={2} pt={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={12}>
                <Typography
                  sx={{
                    wordWrap: 'break-word',
                  }}
                >
                  {selectedCandidatesFile?.file?.name}
                </Typography>
              </Grid>
            </Grid>
            <Grid container pt={0.2} px={2} sx={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={11}>
                <LinearProgress
                  sx={{ p: 0 }}
                  variant="determinate"
                  value={selectedCandidatesFile?.progress}
                />
              </Grid>
              <Grid item xs={1} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    wordWrap: 'break-word',
                  }}
                >{`${Math.round(selectedCandidatesFile?.progress ?? 0)}%`}</Typography>
              </Grid>
              {selectedCandidatesFile?.error && (
                <Grid item>
                  <Typography>{selectedCandidatesFile?.error}</Typography>
                </Grid>
              )}
              {selectedCandidatesFile.processing && !selectedCandidatesFile.completed && (
                <Grid item>
                  <Typography>Processing File</Typography>
                </Grid>
              )}
              {selectedCandidatesFile.completed && (
                <Grid item>
                  <Typography>Processing Completed</Typography>
                </Grid>
              )}
              {selectedCandidatesFile.completed && (
                <Grid container justifyContent="center">
                  <Grid item xs={4} style={{ display: 'flex', justifyContent: 'center' }}>
                    <WsButton
                      onClick={() => {
                        if (
                          methods.getValues('locations') &&
                          methods.getValues('locations') != null
                        ) {
                          setOpenMappingDialog(true);
                        } else {
                          setNotification(warning('Please fill the mandatory prerequisite fields'));
                        }
                      }}
                    >
                      Preview Mapping
                    </WsButton>
                  </Grid>
                  <Grid container sx={{ mt: '1rem' }} justifyContent="center">
                    <Grid item xs={6} style={{ display: 'flex', justifyContent: 'center' }}>
                      <WsButton
                        disabled={methods.getValues('fieldMappings') ? false : true}
                        type="submit"
                      >
                        Confirm Import
                      </WsButton>
                      {errors.fieldMappings && (
                        <FormHelperText error>{errors.fieldMappings.message}</FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {uploadingFor == 'submissions' && (
          <Grid item>
            <Label sx={{ fontWeight: 'bold', my: 2, color: WsColors.TertiaryShades800 }}>
              Note:
            </Label>
            <WsTypography variant={WsTypoVariants.BODY2} bold={true}>
              Please ensure that your file contains exactly 100 candidates, as we are only able to
              process files of this size.
            </WsTypography>
            <Stack
              direction="row"
              justifyContent="flex-start"
              spacing={2}
              mb={2}
              alignItems="center"
            >
              <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SENARY}>
                Sample bulk submission format
              </WsTypography>
              <WsButton
                variant="text"
                startIcon={<WsIconDownload color={WsColors.TheBlues500} />}
                onClick={downloadBulkSubmissionsSampleFile}
              >
                Download File
              </WsButton>
            </Stack>
          </Grid>
        )}
      </Grid>
      <Dialog open={openMappingDialog || errors?.fieldMappings} maxWidth="xl" scroll={'paper'}>
        <DialogTitle>
          <Grid container justifyContent={'space-between'}>
            <Grid item xs={1} display="flex" justifyContent={'start'}>
              <ArrowButton
                title="Go Back"
                onClick={() => setOpenMappingDialog(false)}
              ></ArrowButton>
            </Grid>
            {uploadingFor === 'mapping' && (
              <Grid item xs={7} display="flex" spacing={1} justifyContent={'end'}>
                <Grid item xs={3.7} display="flex" justifyContent={'start'} ml={1}>
                  <Select
                    labelId="select-label-id"
                    id="select-label"
                    name="selectedTemplate"
                    fullWidth
                    size="small"
                    onChange={(event: SelectChangeEvent<number>) => onTemplateSelect(event)}
                    placeholder="Select import template"
                    defaultValue={methods.getValues('selectedTemplate') ?? -1}
                  >
                    <MenuItem key={'Select Template'} value={-1}>
                      Select Template
                    </MenuItem>
                    {templateOption.map((template, index) => (
                      <MenuItem key={index} value={template.value}>
                        {template.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <SaveExcelTemplateModel
                  mappings={methods.watch('fieldMappings') ?? []}
                  getMappings={() => methods.getValues('fieldMappings') ?? []}
                  uploadingFor={uploadingFor}
                  loadTemplates={(value: boolean) => setLoadTemplates(value)}
                  isTemplateSelected={isTemplateSelected}
                />
                <Box sx={{ ml: 1 }}>
                  <WsButton onClick={resetTemplate}>Reset Template</WsButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: '1rem' }}>
            <Typography variant={WsTypoVariants.SUB_TITLE} color="textSecondary">
              Please select appropriate column mappings
            </Typography>
            <Typography variant={WsTypoVariants.BODY1} color="textSecondary">
              *Mapping the email field is mandatory
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={9.5}>
              <Paper elevation={3} style={{ height: '100%' }}>
                <Box p={1}>
                  {errors.fieldMappings && (
                    <FormHelperText error>{errors.fieldMappings.message}</FormHelperText>
                  )}
                  {uploadingFor === 'mapping' ? (
                    <UploadSpreadSheetsColumns
                      candidatesData={excelFields}
                      key={methods.watch('fieldMappings')}
                    />
                  ) : (
                    <>
                      {submissionSetting && (
                        <ImportSpreadSheetColumns
                          fileId={getValues('fileId')}
                          candidatesData={excelFields}
                          submissionSetting={submissionSetting}
                          getFieldMappingValues={getFieldMappingValues}
                          setOpen={setOpenMappingDialog}
                          fieldMappings={methods.getValues('fieldMappings') ?? []}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={2.5}>
              <Paper elevation={3} style={{ height: '100%' }}>
                <Box p={2}>
                  <WsTypography variant={WsTypoVariants.TITLE}>Recent Templates</WsTypography>
                  <Box sx={{ mt: '1rem' }}>
                    {recentTemplates.map((recentTemplate, index) => (
                      <MenuItem
                        key={recentTemplate.value}
                        onClick={() =>
                          onTemplateSelect({
                            target: { value: index },
                          } as SelectChangeEvent<number>)
                        }
                        sx={selectedTemplateIndex === index ? selectedMenuItemStyle : {}}
                      >
                        {recentTemplate.label}
                      </MenuItem>
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        {uploadingFor === 'mapping' && (
          <DialogActions>
            <Box
              px={1}
              py={2}
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <WsButton
                onClick={() => {
                  if (!methods.getValues('fieldMappings').includes('email')) {
                    setNotification(warning('Please Map Email field before proceeding'));
                  } else {
                    clearErrors('fieldMappings');
                    setOpenMappingDialog(false);
                  }
                }}
              >
                Proceed
              </WsButton>
            </Box>
          </DialogActions>
        )}
      </Dialog>
      {openDeleteDialog && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <ConfirmDeleteDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            onConfirm={deleteExcelFileById}
            deletingItem={`${fileToBeDeleted?.current?.name}`}
          />
        </ErrorBoundary>
      )}
    </Grid>
  );
};

export default BulkUploadSpreadSheets;
